import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import ArticleAd from "../ads/ArticleAd";
import { SectionIntro, StatStrip, DeepCard, BulletGrid, FAQBlock, ResearchTable, LinkPills, PillList, ResponsiveComparisonTable, CompactList } from "../components/ContentBlocks";

const jointRows = [
  ["Motor", "Creates rotation before reduction", "Servo motor, BLDC motor or torque motor selected for torque density, heat and control bandwidth"],
  ["Reducer", "Trades motor speed for usable joint torque", "Harmonic drive, cycloidal drive, planetary gearbox or direct drive layout"],
  ["Encoder", "Measures position or speed", "Absolute encoder, incremental encoder or motor side and joint side feedback"],
  ["Brake", "Holds a joint when power is removed", "Important for vertical axes, robot arms, cobots and humanoid limbs"],
  ["Bearing", "Carries radial and axial loads", "Controls stiffness, wobble, wear and payload behavior"],
  ["Torque sensor", "Measures force through the joint or output", "Useful for cobots, force control, safety, contact rich tasks and humanoid control"],
  ["Joint controller", "Runs local control loops and protection", "Reads sensors, drives the motor, watches temperature and handles faults"],
  ["Cable routing", "Moves power and data through the joint", "Affects service life, bend radius, EMI, water resistance and maintenance access"]
];

const componentCards = [
  {
    title: "Harmonic drives",
    description: "Harmonic drives are compact high ratio reducers often used where precision and packaging matter. They can reduce backlash but still need careful torque, fatigue and thermal design.",
    points: ["compact reducer", "high precision", "fatigue and overload limits"]
  },
  {
    title: "Cycloidal drives",
    description: "Cycloidal drives are often selected for high torque and shock tolerance. They can suit industrial arms and heavy joints where impact loads are expected.",
    points: ["shock tolerance", "high torque", "bearing and lubrication quality"]
  },
  {
    title: "Encoders and brakes",
    description: "Encoders tell the controller where the joint is. Brakes help hold position when power is off or when the axis is vertical. Weak feedback creates drift, jitter and unsafe recovery behavior.",
    points: ["absolute position", "holding force", "safe restart behavior"]
  },
  {
    title: "Bearings and stiffness",
    description: "Bearings and structure decide how much the joint flexes under payload. A robot can have a strong motor and still lose precision because of compliance, backlash or worn bearings.",
    points: ["payload path", "joint stiffness", "wear under duty cycle"]
  }
];

const motionControlCards = [
  {
    title: "Drive and feedback hardware",
    description: "Motor drivers and servo drives deliver power, while encoders, current sensing and torque sensors return measurements. Brakes and safety inputs add constraints that the joint controller must respect.",
    points: ["drive electronics", "encoder feedback", "current, torque and safety signals"]
  },
  {
    title: "Closed-loop behavior",
    description: "A high torque motor can still move badly if the encoder is noisy, the brake releases late, the reducer has backlash or the controller overheats. Test these parts together under the intended load and duty cycle.",
    points: ["closed loop behavior", "latency and jitter", "thermal and backlash limits"]
  }
];


const pointCardColumns = [
  { key: "title", label: "Topic" },
  { key: "description", label: "Technical role" },
  { key: "points", label: "Checks" }
];

function PointCardTable({ rows, caption }) {
  const tableRows = rows.map((row) => ({
    ...row,
    points: <CompactList items={row.points} />
  }));

  return (
    <ResponsiveComparisonTable
      columns={pointCardColumns}
      rows={tableRows}
      caption={caption}
      ariaLabel={caption}
      minWidth="min-w-[850px]"
    />
  );
}

const exampleCards = [
  {
    title: "Industrial robot arms",
    description: "Factory arms prioritize repeatability, uptime, payload, cycle time and service access. The joints must survive long duty cycles and predictable maintenance windows."
  },
  {
    title: "Collaborative robots",
    description: "Cobots add force limits, safety monitoring and human proximity. Torque sensing, brakes and compliant control become part of the safety story."
  },
  {
    title: "Humanoids",
    description: "Humanoid joints need compact packaging, heat control, cable routing, impact tolerance and enough torque for balance, arms, hands and recovery motions."
  },
  {
    title: "Quadrupeds",
    description: "Legged robots need joints that handle repeated impacts, fast torque changes, dust, vibration and thermal load during walking, stairs or rough ground."
  }
];

const designLimits = [
  "Thermal limits can force a joint to reduce torque before the task is finished.",
  "Backlash can make precise placement harder even if the motor encoder looks accurate.",
  "Cable fatigue can stop a robot that otherwise has healthy motors and reducers.",
  "Higher payload usually increases bearing load, frame stress and brake requirements.",
  "More stiffness improves precision but can reduce shock absorption and raise cost."
];

const faqs = [
  {
    question: "What components are inside a robotic arm joint?",
    answer: "A typical robotic arm joint can include a motor, reducer, encoder, brake, bearing, torque sensor, joint controller, cables, connectors, housing and thermal path."
  },
  {
    question: "Why do robotic arm joints use reducers?",
    answer: "Reducers convert motor speed into useful torque at the joint. They also affect backlash, stiffness, efficiency, shock tolerance, heat and maintenance."
  },
  {
    question: "What is backlash in a robot joint?",
    answer: "Backlash is lost motion in the transmission. It can reduce precision, create vibration and make control harder, especially under changing loads."
  },
  {
    question: "Are humanoid joints the same as industrial robot arm joints?",
    answer: "They share motors, reducers, encoders and bearings, but humanoid joints face tighter packaging, balance, impact, battery and heat constraints."
  }
];

export default function RoboticArmJointComponents() {
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Robot Components", to: "/robot-components" },
    { label: "Robotic Arm Joint Components", to: "/robotic-arm-joint-components" }
  ];
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Robotic Arm Joint Components",
    description: "A technical guide to motors, reducers, encoders, brakes, bearings, torque sensors and cable routing inside robot arm joints.",
    author: { "@type": "Organization", name: "TechniaHQRobot" }
  };

  return (
    <div className="article-page">
      <SEO
        title="Robotic Arm Joint Components"
        description="See what sits inside a robot arm joint motors, encoders, brakes, harmonic drives, cycloidal drives, bearings, torque sensors and limits."
        path="/robotic-arm-joint-components"
        type="article"
        schema={[buildBreadcrumbSchema(breadcrumbs), schema]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow="Robot hardware anatomy"
        title="Robotic arm joint components explained clearly"
        description="A robot joint is not just a motor. It is a mechanical, electrical and control module where torque, stiffness, feedback, heat, cables, backlash, payload and safety all meet."
      />

      <section className="site-shell py-10 sm:py-14">
        <ArticleAd placement="top" articleSlug="robotic-arm-joint-components" className="mb-8" />
        <StatStrip items={[
          { value: "8 parts", label: "motor, reducer, encoder, brake, bearing, torque sensor, controller, cables" },
          { value: "joint limits", label: "heat, backlash, stiffness and payload" },
          { value: "4 robot classes", label: "arms, cobots, humanoids and quadrupeds" },
          { value: "hardware + control", label: "mechanics connected to software" }
        ]} />
      </section>

      <section className="site-shell py-12 sm:py-16">
        <SectionIntro
          eyebrow="Joint stack"
          title="What is inside a robotic arm joint"
          description="The parts below work together. A stronger motor cannot fix poor cable routing. A high resolution encoder cannot remove gearbox backlash. A compact reducer can still fail if heat and shock loads are ignored."
        />
        <ResearchTable columns={["Component", "Role", "Engineering note"]} rows={jointRows} />
      </section>

      <section className="site-shell py-12 sm:py-16">
        <SectionIntro
          eyebrow="Motion control"
          title="Robot motion control components inside a joint"
          description="Motion control connects the mechanical joint to the software stack. These components decide whether a robot arm tracks a path smoothly, holds position safely and recovers after a fault."
        />
        <PointCardTable rows={motionControlCards} caption="Robot motion control component comparison" />
      </section>

      <section className="site-shell py-12 sm:py-16">
        <SectionIntro
          eyebrow="Key components"
          title="The parts that decide precision, torque and service life"
          description="Robotic arm joint components are selected around the task. Welding arms, cobots, humanoids and quadrupeds need different tradeoffs."
        />
        <PointCardTable rows={componentCards} caption="Robotic arm joint component comparison" />
      </section>

      <section className="site-shell py-12 sm:py-16">
        <div className="soft-card bg-neutral-50/90 p-6 sm:p-8">
          <SectionIntro
            eyebrow="Failure modes"
            title="Why a robot joint can fail even with good parts"
            description="Joint design is about the full system under load. Heat, vibration, cable wear, payload changes, lubrication, impact and software faults appear during real operation."
            action={<LinkPills items={[{ label: "Robot components", to: "/robot-components" }, { label: "Robot sensors", to: "/robot-sensors" }, { label: "Robotics software", to: "/robotics-software" }, { label: "Industrial robots", to: "/robot-types/industrial-robots" }]} />}
          />
          <PillList items={["servo motors", "BLDC motors", "harmonic drives", "cycloidal drives", "encoders", "brakes", "bearings", "torque sensors", "cable routing"]} />
          <div className="mt-6">
            <BulletGrid title="Design constraints" items={designLimits} />
          </div>
        </div>
      </section>

      <section className="site-shell py-12 sm:py-16">
        <SectionIntro
          eyebrow="Examples"
          title="How joint requirements change by robot type"
          description="The same component vocabulary appears across robot arms, cobots, humanoids and quadrupeds. The design priority changes with payload, speed, environment and safety envelope."
        />
        <div className="grid gap-4 md:grid-cols-2">
          {exampleCards.map((item) => <DeepCard key={item.title} title={item.title} description={item.description} />)}
        </div>
      </section>

      <section className="site-shell pb-10 sm:pb-14">
        <ArticleAd placement="bottom" articleSlug="robotic-arm-joint-components" />
      </section>

      <FAQBlock items={faqs} />
      <CTASection title="Follow TechniaHQRobot for robot hardware breakdowns" />
    </div>
  );
}
